import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog, LeaveBalanceBanner } from '@gokrew/ui';
import { useDialog } from '@gokrew/ui/context';
import { TranslationNS } from '@gokrew/models/constants';
import type { MyTeamApprovalRequest } from '@gokrew/models/my-team';

/**
 * Approving a request the balance cannot cover needs an explicit "Approve
 * anyway" (the balance then goes negative). Any other request approves at
 * once. Shared by the Approvals tab and the dashboard "To approve" card.
 */
export function useConfirmApprove() {
  const { t } = useTranslation(TranslationNS.MyTeam);
  const { t: tLeave } = useTranslation(TranslationNS.LeaveManagement);
  const { openDialog } = useDialog();

  return useCallback(
    (request: MyTeamApprovalRequest, approve: () => void) => {
      const balance = request.balanceCheck;
      if (!balance?.insufficient) {
        approve();
        return;
      }
      openDialog({
        title: t('approvals.approve_anyway_title', {
          name: request.employeeName,
        }),
        containerClass: 'w-full md:w-xl md:max-w-none',
        content: (
          <ConfirmationDialog
            variant="destructive"
            confirmLabel={t('approvals.approve_anyway', {
              balance: tLeave('leave_decision.days', { count: balance.after }),
            })}
            onSubmit={approve}
          >
            <LeaveBalanceBanner balance={balance} />
          </ConfirmationDialog>
        ),
      });
    },
    [openDialog, t, tLeave]
  );
}
